'use client'

import { useEffect, useState } from 'react'
import { BT, labelStyle } from './tokens'

interface TocItem {
    id: string
    text: string
    level: 2 | 3
}

const STICKY_OFFSET = 120

// TOC + прогресс-бар. ЕДИНЫЙ источник: scrollY + offsetTop заголовков.
// Активный пункт — последний heading, чей offsetTop <= (scrollY + STICKY_OFFSET).
// Прогресс — scrollY / (scrollHeight - innerHeight). Никакого IntersectionObserver,
// иначе active и progress рассинхронизируются на длинных разделах.
export function TableOfContents() {
    const [items, setItems] = useState<TocItem[]>([])
    const [activeId, setActiveId] = useState<string | null>(null)
    const [progress, setProgress] = useState(0)

    useEffect(() => {
        const container = document.querySelector<HTMLElement>('.blog-article')
        if (!container) return

        let headings: HTMLElement[] = []

        const recompute = () => {
            if (headings.length === 0) return
            const scrollY = window.scrollY
            const mark = STICKY_OFFSET

            // getBoundingClientRect().top — от viewport, сравниваем с STICKY_OFFSET.
            // offsetTop нельзя: он от offsetParent, рассинхрон с window.scrollY.
            let active: string | null = headings[0].id
            for (const h of headings) {
                if (h.getBoundingClientRect().top <= mark) active = h.id
                else break
            }

            const docHeight = document.documentElement.scrollHeight - window.innerHeight
            const pct = docHeight > 0 ? (scrollY / docHeight) * 100 : 0

            // Если долистали до низа — принудительно последний heading.
            // Короткий последний раздел не успевает пересечь STICKY_OFFSET.
            if (docHeight > 0 && scrollY >= docHeight - 2) {
                active = headings[headings.length - 1].id
            }

            setActiveId((prev) => (prev === active ? prev : active))
            setProgress(Math.min(100, Math.max(0, pct)))
        }

        const syncHeadings = () => {
            headings = Array.from(container.querySelectorAll<HTMLElement>('h2, h3')).filter(
                (h) => !!h.id,
            )
            setItems(
                headings.map((h) => ({
                    id: h.id,
                    text: (h.textContent || '').trim(),
                    level: (h.tagName === 'H2' ? 2 : 3) as 2 | 3,
                })),
            )
            recompute()
        }

        // Сервер уже проставляет id на заголовках в HTML — DOM готов сразу.
        syncHeadings()
        window.addEventListener('scroll', recompute, { passive: true })
        window.addEventListener('resize', recompute, { passive: true })
        return () => {
            window.removeEventListener('scroll', recompute)
            window.removeEventListener('resize', recompute)
        }
    }, [])

    if (items.length < 2) return null

    return (
        <>
            <ol
                className="m-0 flex list-none flex-col gap-0 p-0"
                style={{ borderTop: `1px solid ${BT.ink}` }}
            >
                {items.map((it, i) => {
                    const active = it.id === activeId
                    return (
                        <li key={it.id} style={{ borderBottom: `1px solid ${BT.rule}` }}>
                            <a
                                href={`#${it.id}`}
                                className="flex gap-2.5 py-2.5 no-underline"
                                style={{
                                    color: active ? BT.accent : BT.ink,
                                    fontSize: 13,
                                    lineHeight: 1.4,
                                    fontWeight: active ? 600 : 400,
                                    paddingLeft: it.level === 3 ? 18 : 0,
                                }}
                            >
                                <span
                                    style={{
                                        ...labelStyle,
                                        color: active ? BT.accent : BT.subDim,
                                        minWidth: 24,
                                    }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span>{it.text}</span>
                            </a>
                        </li>
                    )
                })}
            </ol>
            <div
                className="mt-6 overflow-hidden rounded-full"
                style={{ height: 3, background: BT.rule }}
            >
                <div
                    style={{
                        height: '100%',
                        width: `${progress}%`,
                        background: BT.accent,
                        transition: 'width 0.1s linear',
                    }}
                />
            </div>
            <div style={{ ...labelStyle, marginTop: 8 }}>
                прогресс · {Math.round(progress)}%
            </div>
        </>
    )
}
